import type { BottomSheetConfig, BottomSheetController } from './types';

import { usePropertiesListingHeader } from '~/composables/usePropertiesListingHeader';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import { createAsyncRegistry } from './asyncRegistry';
import {
	BARRIER_ARM_DURATION_MS,
	BOTTOM_SHEET_DRAG_ZONE_ATTR,
	CLOSE_FROM_FULLY_OPEN_DURATION_MS,
	DEFAULT_BREAKPOINT,
	DEFAULT_TOP_CHROME,
	easeOutCubic,
	easeOutSeptic,
	HANDLE_CLICK_GUARD_MS,
	LIST_SCROLL_START_BUFFER_PX,
	MIN_SNAP_DELTA_PX,
	PREVIOUS_LEVEL,
	SCROLL_IDLE_MS,
	SNAP_DIRECTION_PROGRESS_RATIO,
	SNAP_LEVELS,
	SNAP_RESTING_PX
} from './constants';
import { useSheetGeometry } from './geometry';
import { useSheetHeader } from './header';
import {
	finishListScrollInteraction,
	updateListScrollInteraction
} from './interactionPolicy';
import { useSheetMachine } from './machine';
import { usePhysicalBarrier } from './physicalBarrier';
import { createSnapAnimator, resolveDirectionalSnap } from './snapEngine';
import { createTouchDirectionTracker } from './touchDirectionTracker';
import { SheetLevel } from './types';

// Orquestrador do bottom sheet.
// Mantem o comportamento atual e delega calculos para modulos focados.

export function useBottomSheet(
	config: BottomSheetConfig
): BottomSheetController {
	const {
		snapHeights,
		topChromeHeight = DEFAULT_TOP_CHROME,
		closedTopChromeHeight = topChromeHeight,
		spacerElement,
		breakpoint = DEFAULT_BREAKPOINT,
		disabled
	} = config;

	const isScreenBelowBreakpoint = ref(false);
	const viewportHeight = ref(0);
	// Always VIRTUAL coordinates. Converted to/from real window.scrollY at
	// every DOM boundary via the barrier helpers below.
	const scrollY = ref(0);

	// Todas as regras de visibilidade da navbar vivem no controlador.
	const header = useSheetHeader(() => isScreenBelowBreakpoint.value);

	const geometry = useSheetGeometry(
		snapHeights,
		viewportHeight,
		topChromeHeight,
		closedTopChromeHeight
	);
	const machine = useSheetMachine();
	const { isBottomSheetDragging } = usePropertiesListingHeader();
	watch(
		() => machine.isDragging.value || machine.isTransitioning.value,
		isActive => {
			isBottomSheetDragging.value = isActive;
		}
	);
	const animator = createSnapAnimator();
	const barrier = usePhysicalBarrier(geometry.spacerExtent, spacerElement);

	// Registro unico de timers/rafs para cleanup.
	const registry = createAsyncRegistry(
		['scrollIdle'] as const,
		['drag', 'arm'] as const
	);

	const hasInitialized = ref(false);

	const currentLevel = computed<SheetLevel>(() => {
		// Antes da inicializacao, assume CLOSED para manter UI estavel.
		if (!hasInitialized.value) return SheetLevel.CLOSED;
		return geometry.levelAt(scrollY.value);
	});

	// Sheet cobrindo a viewport (abaixo do chrome): da ancora inicial (logo
	// abaixo de navbar + filtros) ate o CLOSED e alem (leitura). Vira false no
	// primeiro pixel da descida. Mesma fronteira que `isInReadingMode()`.
	const isSheetFullscreen = computed(() => isInReadingMode());

	const isTransitioning = machine.isTransitioning;
	const isDragging = machine.isDragging;

	// Guard de clique sintetico apos drag.
	let suppressClickUntil = 0;
	// Controla listeners globais de touch durante drag da alca.
	let hasWindowDragListeners = false;

	// Helpers de conversao entre coordenadas virtual <-> real.

	function readVirtualScrollY(): number {
		return barrier.toVirtual(Math.max(0, window.scrollY));
	}

	function writeVirtualScrollY(virtual: number): void {
		window.scrollTo(0, barrier.toReal(virtual));
	}

	// Escreve no DOM e atualiza o estado reativo local.
	function commitVirtualScroll(virtual: number): void {
		writeVirtualScrollY(virtual);
		scrollY.value = virtual;
	}

	// Snaps em Y virtual. SNAP_LEVELS ja esta em ordem ascendente de posicao
	// (FULLY_OPEN < HALF_OPEN < CLOSED) e a geometria garante essa ordem nos
	// valores, entao o map ja sai ascendente.
	function getSnapTargetsAscending(): number[] {
		return SNAP_LEVELS.map(lvl => geometry.snapScrollY.value[lvl]);
	}

	// True quando o scroll passou da faixa do drawer (lista em leitura).
	function isInReadingMode(): boolean {
		return scrollY.value >= geometry.spacerExtent.value - SNAP_RESTING_PX;
	}

	// Scroll da lista: snapa dentro da faixa do drawer.

	const supportsScrollEnd = import.meta.client && 'onscrollend' in window;

	let lastScrollY = 0;
	// Flag que indica se o scroll estah em progresso. Apenas false apos scrollend.
	// Usado para evitar recalc de geometria no meio de momentum scroll.
	const isScrolling = ref(false);
	// Posicao virtual onde o gesto atual da lista comecou (definida no toque).
	// Ancora a direcao liquida do gesto ao escolher o snap final.
	let listGestureStartY = 0;
	// So assenta num snap E so mexe na navbar quando houve gesto do usuario na
	// lista (touchstart). Scrolls sem gesto — posicao inicial, restauracao de
	// scroll do browser/router no F5 — chegam com `isTrusted` e, sem essa flag,
	// puxariam o sheet pro snap mais proximo ou esconderiam a navbar na carga.
	let hasPendingListGesture = false;
	// Ate a primeira interacao (toque ou API), a pagina esta "pristina":
	// qualquer scroll que chegue e ruido de carga (scroll-to-top/restauracao
	// do roteador) e e corrigido de volta pra ancora — sem timers.
	let isAwaitingFirstInteraction = true;

	// Flag que marca se a altura do viewport precisa ser aplicada quando scrollend dispara.
	// Usado pra deferir applyViewportHeight durante scroll (evita reflow mid-momentum).
	let isViewportHeightPending = false;

	// True enquanto o arme GRADUAL da barreira esta rodando (v3). Durante ele o
	// scroll real e dirigido por nos (nao pela coordenada virtual), entao o
	// onScroll ignora os eventos para nao reinterpretar a posicao.
	let isArming = false;
	// Posicao visual alvo do arme em curso — usada para finaliza-lo na hora se
	// o usuario tocar a tela (abort-on-touch), liberando o scroll.
	let armTargetVisual = 0;

	function onScroll(event: Event): void {
		if (!hasInitialized.value) return;
		// Durante o arme gradual nos mesmos dirigimos o scroll real; ignorar.
		if (isArming) return;
		const y = readVirtualScrollY();
		const previousY = lastScrollY;
		const delta = y - lastScrollY;
		const isUserScroll = event.isTrusted;

		lastScrollY = y;
		scrollY.value = y;

		// Isola completamente o ciclo de scroll nativo de qualquer gesto ou animação ativos.
		// Evita que eventos de rolagem atrasados modifiquem a navbar ou armem barreiras durante drag/snap.
		if (
			machine.isDragging.value ||
			machine.isTransitioning.value ||
			animator.isAnimating()
		) {
			return;
		}

		// Marca que scroll estah em progresso (flag zerada pelo scrollend).
		if (isUserScroll) {
			isScrolling.value = true;
		}
		// Ruido de carga: antes da primeira interacao, reancora em vez de
		// aceitar o scroll (roteador/browser rolando a janela apos o mount).
		if (isAwaitingFirstInteraction && !animator.isAnimating()) {
			if (Math.abs(y - geometry.spacerExtent.value) > SNAP_RESTING_PX) {
				commitVirtualScroll(geometry.spacerExtent.value);
			}
			return;
		}

		if (hasPendingListGesture && !animator.isAnimating()) {
			header.trackReadingScroll(delta, isInReadingMode());
		}

		// NAO armamos a barreira durante o scroll (instantaneo): um `scrollTo` de
		// ~spacerExtent no meio do momentum o iOS interpreta como forca extra e
		// "dispara" o scroll (bug raro do half->closed). O arme acontece no
		// settle (onScrollEnd), de forma GRADUAL — depois do movimento parar.
		const decision = updateListScrollInteraction({
			y,
			previousY,
			isUserScroll,
			isScreenBelowBreakpoint: isScreenBelowBreakpoint.value,
			isAnimating: animator.isAnimating(),
			maxOffset: geometry.maxOffset.value,
			restingPx: SNAP_RESTING_PX,
			buffer: LIST_SCROLL_START_BUFFER_PX,
			supportsScrollEnd
		});

		if (decision.shouldScheduleScrollEnd) {
			registry.clearTimer('scrollIdle');
			registry.timers.scrollIdle = setTimeout(onScrollEnd, SCROLL_IDLE_MS);
		}
	}

	function onScrollEnd(): void {
		// Durante o arme gradual NOS dirigimos o scroll real (varios `scrollTo`),
		// e cada um deles dispara um `scrollend` nativo (Chrome e iOS >= 16.4).
		// Sem este guard, esse `scrollend` chamaria `settleBarrier` -> `armBarrierGradually`,
		// que faz `cancelRaf('arm')` e REINICIA o arme do zero — o arme nunca chega a
		// finalizar (`finalizeArm`), a barreira nunca arma e o spacer fica preso
		// oscilando com o scroll (tremor). Espelha o mesmo guard de `onScroll`.
		if (isArming) return;
		if (
			machine.isDragging.value ||
			machine.isTransitioning.value ||
			animator.isAnimating()
		) {
			return;
		}

		isScrolling.value = false;
		// Se houver resize pendente, aplica agora que o scroll parou.
		if (isViewportHeightPending) {
			isViewportHeightPending = false;
			applyViewportHeight();
		}
		const y = scrollY.value;

		// Ja na ancora/leitura do fullscreen: nao snapa, mas arma a barreira
		// GRADUALMENTE agora que o scroll parou (substitui a arme instantanea que
		// fazia o scroll "disparar" no meio do momentum). Garante o stick ao
		// voltar pro topo. No-op se ja estiver armada.
		if (isInReadingMode()) {
			settleBarrier();
			return;
		}

		const action = finishListScrollInteraction({
			y,
			maxOffset: geometry.maxOffset.value,
			restingPx: SNAP_RESTING_PX,
			isScreenBelowBreakpoint: isScreenBelowBreakpoint.value,
			isAnimating: animator.isAnimating(),
			isDragging: machine.isDragging.value
		});

		if (action === 'ignore') {
			return;
		}
		if (!hasPendingListGesture) return;
		hasPendingListGesture = false;
		settleGesture(listGestureStartY);
	}

	// Finaliza o arme: fixa o estado armado e ressincroniza a coordenada virtual.
	function finalizeArm(): void {
		registry.rafs.arm = null;
		isArming = false;
		barrier.setArmed(true, armTargetVisual);
		scrollY.value = readVirtualScrollY();
	}

	// v3: arme GRADUAL da barreira. O arme instantaneo da `setArmed` faz um
	// `scrollTo` de ~spacerExtent ate o topo do documento — um pulo de scroll
	// que o iOS interpreta como "scroll pra cima" e ANIMA a barra de URL; esse
	// resize da visual viewport re-compoe as sobreposicoes do 1o card (o pisca).
	// Movendo spacer + scroll JUNTOS ao longo de ~300ms, a barra de URL se move
	// de forma continua (como num scroll do usuario, que comprovadamente NAO
	// pisca) em vez do salto instantaneo. Posicao visual fica estavel o tempo
	// todo (spacer encolhe na mesma medida que o scroll desce).
	function armBarrierGradually(currentVisual: number): void {
		if (barrier.isArmed.value) return;
		const spacerEl = spacerElement?.value;
		const full = geometry.spacerExtent.value;
		if (!spacerEl || full <= 0) {
			barrier.setArmed(true, currentVisual);
			return;
		}
		registry.cancelRaf('arm');
		isArming = true;
		armTargetVisual = currentVisual;

		const startTime = performance.now();
		const visualConstant = full - currentVisual;
		const step = (now: number): void => {
			const t = Math.min(1, (now - startTime) / BARRIER_ARM_DURATION_MS);
			const offset = full * easeOutCubic(t);
			// CHAVE anti-tremor: o browser arredonda `scrollY` pra inteiro, mas o
			// spacer aceita fracionario. Se eu setar os dois com o mesmo valor
			// fracionario, `spacerHeight - scrollY` (a posicao visual) varia
			// sub-pixel a cada frame -> tremor nas bordas. Solucao: arredondar o
			// scroll para inteiro e DERIVAR o spacer desse mesmo inteiro, de modo
			// que `spacerHeight - scrollY = full - currentVisual` EXATO todo frame.
			const realScroll = Math.max(0, Math.round(currentVisual - offset));
			spacerEl.style.height = `${Math.max(0, visualConstant + realScroll)}px`;
			// Força o layout do spacer ANTES do scrollTo no mesmo frame, senao o
			// scroll opera na altura antiga do documento e clampa errado.
			void spacerEl.offsetHeight;
			window.scrollTo(0, realScroll);
			if (t < 1) {
				registry.rafs.arm = requestAnimationFrame(step);
				return;
			}
			finalizeArm();
		};
		registry.rafs.arm = requestAnimationFrame(step);
	}

	// Aborta o arme gradual em curso e finaliza o estado armado NA HORA.
	// Chamado quando o usuario toca a tela durante o arme: sem isso, o arme
	// fica ~300ms chamando `scrollTo` e "segura" o scroll do usuario. Como o
	// dedo ja esta na tela (gesto ativo), o ajuste final e mascarado.
	function finishArmImmediately(): void {
		if (!isArming) return;
		registry.cancelRaf('arm');
		finalizeArm();
	}

	// Assenta a barreira pro repouso do nivel atual. No CLOSED (fullscreen), a barreira
	// fica armada para que o scroll nativo pare no topo da lista. Nos demais niveis,
	// ela fica desarmada para permitir movimentar/arrastar o drawer.
	function settleBarrier(): void {
		const level = currentLevel.value;
		if (level === SheetLevel.CLOSED) {
			armBarrierGradually(scrollY.value);
		} else {
			// Desarmar rola pra BAIXO (afasta do topo) — nao anima a barra de URL,
			// entao mantem instantaneo.
			registry.cancelRaf('arm');
			isArming = false;
			barrier.setArmed(false, scrollY.value);
		}
	}

	// Snap final de um gesto — MESMA regra para drag da alca e scroll da lista:
	// segue a DIRECAO do gesto a partir de onde ele comecou.
	function settleGesture(startY: number): void {
		const y = scrollY.value;
		const closedY = geometry.snapScrollY.value[SheetLevel.CLOSED];

		const target = resolveDirectionalSnap({
			startY,
			y,
			snapsAscending: getSnapTargetsAscending(),
			progressRatio: SNAP_DIRECTION_PROGRESS_RATIO,
			restingPx: SNAP_RESTING_PX
		});
		header.revealOnPullBackToClosed({ startY, y, target, closedY });

		// O fullscreen tem DOIS repousos: com a navbar visivel a lista assenta na
		// ancora (abaixo do chrome completo, como no estado inicial); com a navbar
		// oculta assenta no CLOSED (colada na barra de filtros). Sem isso, voltar
		// da leitura com a navbar revelada deixaria ela COBRINDO o topo da lista.
		const isClosedTarget = target >= closedY - SNAP_RESTING_PX;
		const restTarget =
			isClosedTarget && header.isVisible()
				? geometry.spacerExtent.value
				: target;

		if (Math.abs(restTarget - y) > MIN_SNAP_DELTA_PX) snapTo(restTarget);
		else settleBarrier();
	}

	// Move para um alvo de snap, animado (ou instantaneo se ja esta no alvo).
	function snapTo(target: number): void {
		// Um tap na alca termina primeiro como touch e depois dispara o click
		// sintetico. O touchend pode ter iniciado o arme gradual da barreira;
		// finalize-o antes do snap para nao deixar dois RAFs escrevendo spacer e
		// scroll ao mesmo tempo.
		finishArmImmediately();
		const start = scrollY.value;
		if (Math.abs(target - start) < MIN_SNAP_DELTA_PX) {
			commitVirtualScroll(target);
			settleBarrier();
			header.syncToLevel(currentLevel.value);
			return;
		}
		// So desarma quando o alvo fica abaixo do fullscreen (rumo a half/peek).
		if (target < geometry.spacerExtent.value) {
			barrier.setArmed(false, start);
		}
		machine.beginSnap(start, target);

		const targetLevel = geometry.levelAt(target);
		header.syncToLevel(targetLevel);

		// Fechar a partir do CLOSED (totalmente aberto) rumo a um nivel menor usa
		// uma desaceleracao mais suave e longa (ver constantes).
		const closedY = geometry.snapScrollY.value[SheetLevel.CLOSED];
		const isClosingFromFullyOpen =
			start >= closedY - SNAP_RESTING_PX && target < start;

		animator.animate({
			from: start,
			to: target,
			onTick: y => writeVirtualScrollY(y),
			customDuration: isClosingFromFullyOpen
				? CLOSE_FROM_FULLY_OPEN_DURATION_MS
				: undefined,
			customEasing: isClosingFromFullyOpen ? easeOutSeptic : undefined,
			onDone: () => {
				scrollY.value = target;
				machine.endSnap();
				settleBarrier();
				header.syncToLevel(currentLevel.value);
			}
		});
	}

	// Gestos de touch da alca.

	function flushDragScroll(): void {
		registry.rafs.drag = null;
		const phase = machine.phase.value;
		if (phase.kind !== 'dragging') return;
		writeVirtualScrollY(phase.pendingTargetY);
		scrollY.value = readVirtualScrollY();
	}

	function scheduleDragFrame(): void {
		if (registry.rafs.drag === null) {
			registry.rafs.drag = requestAnimationFrame(flushDragScroll);
		}
	}

	function attachWindowDragListeners(): void {
		if (hasWindowDragListeners || !import.meta.client) return;
		window.addEventListener('touchmove', onHandleTouchMove, { passive: false });
		window.addEventListener('touchend', onHandleTouchEnd, { passive: true });
		window.addEventListener('touchcancel', onHandleTouchEnd, { passive: true });
		hasWindowDragListeners = true;
	}

	function detachWindowDragListeners(): void {
		if (!hasWindowDragListeners || !import.meta.client) return;
		window.removeEventListener('touchmove', onHandleTouchMove);
		window.removeEventListener('touchend', onHandleTouchEnd);
		window.removeEventListener('touchcancel', onHandleTouchEnd);
		hasWindowDragListeners = false;
	}

	function onHandleTouchStart(e: TouchEvent): void {
		if (disabled?.value) return;
		if (!isScreenBelowBreakpoint.value) return;
		isAwaitingFirstInteraction = false;
		// Toque durante o arme gradual: finaliza-o na hora para nao segurar o gesto.
		finishArmImmediately();

		// NAO chamar preventDefault no touchstart: cancelar o inicio do toque
		// suprime o click sintetico e mata o tap no CTA ("toque ou arraste").
		// O scroll nativo a partir da alca ja e bloqueado pelo
		// `touch-action: none` do CSS; o preventDefault de drag acontece no
		// touchmove (onHandleTouchMove).
		const t = e.touches[0];
		if (!t) return;

		// Sincroniza a navbar antes do drag (previne flicks de transicoes pendentes).
		const level = currentLevel.value;
		header.syncToLevel(level);

		// Desarma a barreira imediatamente ao iniciar o arrasto (drag) para permitir
		// movimentar o drawer para baixo.
		if (barrier.isArmed.value) {
			barrier.setArmed(false, scrollY.value);
			scrollY.value = readVirtualScrollY();
		}

		animator.cancel();
		machine.endSnap();
		const startVirtual = scrollY.value;
		machine.beginDrag({
			startY: t.clientY,
			startScrollY: startVirtual,
			startLevel: level
		});
		attachWindowDragListeners();
	}

	// Traduz deslocamento do dedo para alvo de scroll virtual.
	function computeDragTarget(
		dy: number,
		startScrollY: number,
		startLevel: SheetLevel
	): number {
		const startedFromPeek =
			startLevel === SheetLevel.FULLY_OPEN && startScrollY <= SNAP_RESTING_PX;
		if (startedFromPeek) return Math.abs(dy);
		// Permite seguir para modo leitura no mesmo gesto.
		return Math.max(startScrollY - dy, 0);
	}

	function onHandleTouchMove(e: TouchEvent): void {
		const phase = machine.phase.value;
		if (phase.kind !== 'dragging') return;
		const t = e.touches[0];
		if (!t) return;
		if (e.cancelable) e.preventDefault();
		const dy = t.clientY - phase.startY;
		const target = computeDragTarget(dy, phase.startScrollY, phase.startLevel);

		machine.updateDrag(dy, target);
		scheduleDragFrame();
	}

	function onHandleTouchEnd(): void {
		const phase = machine.phase.value;
		if (phase.kind !== 'dragging') return;
		const isDrag = machine.wasDragGesture.value;
		const startScrollY = phase.startScrollY;
		const pendingTargetY = phase.pendingTargetY;

		detachWindowDragListeners();
		registry.cancelRaf('drag');
		if (isDrag) {
			suppressClickUntil = Date.now() + HANDLE_CLICK_GUARD_MS;
		}

		// Comita ultima posicao antes do snap final.
		writeVirtualScrollY(pendingTargetY);
		scrollY.value = readVirtualScrollY();
		machine.endDrag();

		// Tap (sem arrasto): assenta no nivel atual; o clique trata o toggle.
		if (!isDrag) {
			settleBarrier();
			return;
		}

		// Arrasto amplo a partir do peek pode terminar em modo leitura: nesse caso
		// so armamos a barreira e preservamos a posicao de leitura. Sem este guard,
		// o gesto cai em `settleGesture`, que com a navbar oculta resolve o repouso
		// para CLOSED (maxOffset) e puxa o sheet pra baixo, perdendo o scroll.
		if (isInReadingMode()) {
			settleBarrier();
			return;
		}

		// A barreira ja foi liberada em `onHandleTouchMove` caso o gesto tenha
		// cruzado abaixo do half; o `snapTo`/`settleBarrier` cuidam do estado
		// final, entao nao mexemos na barreira aqui.
		settleGesture(startScrollY);
	}

	// Evita click sintetico logo apos drag.
	function shouldIgnoreHandleClick(): boolean {
		return Date.now() < suppressClickUntil;
	}

	// API imperativa usada pela pagina.

	// Faz snap para um nivel especifico.
	function scrollToLevel(level: SheetLevel, instant = false): void {
		if (disabled?.value) return;
		if (!isScreenBelowBreakpoint.value) return;
		isAwaitingFirstInteraction = false;
		const target = geometry.snapScrollY.value[level];
		if (instant) {
			// O caminho instantaneo nao passa por snapTo, mas precisa da mesma
			// exclusao mutua com o RAF do arme gradual.
			finishArmImmediately();
			animator.cancel();
			machine.endSnap();
			barrier.setArmed(level === SheetLevel.CLOSED, scrollY.value);
			commitVirtualScroll(target);
			return;
		}
		snapTo(target);
	}

	// Volta um nivel (CLOSED -> HALF -> FULLY).
	function moveBackOneLevel(): void {
		const next = PREVIOUS_LEVEL[currentLevel.value];
		if (next !== undefined) scrollToLevel(next);
	}

	// Volta para posicao inicial (peek).
	function moveBackToInitialPosition(): void {
		scrollToLevel(SheetLevel.FULLY_OPEN);
	}

	// Fecha para peek quando nao estiver no peek.
	function toggleToClosedWhenOpen(): void {
		if (currentLevel.value !== SheetLevel.FULLY_OPEN) {
			scrollToLevel(SheetLevel.FULLY_OPEN);
		}
	}

	// Abre para half-open quando estiver no peek.
	function toggleToHalfOpenWhenClosed(): void {
		if (currentLevel.value === SheetLevel.FULLY_OPEN) {
			scrollToLevel(SheetLevel.HALF_OPEN);
		}
	}

	// Lifecycle e sincronizacao com viewport.

	// Atualiza flags de breakpoint e altura da viewport.
	//
	// Largura/breakpoint sao imediatos (rotacao). A ALTURA, porem, e adiada quando
	// o scroll esta ativo: a barra de endereco dinamica do mobile dispara `resize`
	// no meio do scroll, e atualizar `viewportHeight` aqui encolheria o spacer
	// (reativo) ~100px, fazendo o documento refluir e travar a inercia — pior perto
	// do rodape. Aplicamos a altura so quando os resizes pausam (~SCROLL_IDLE_MS).
	function applyViewportHeight(): void {
		if (!import.meta.client) return;
		const nextViewportHeight = window.innerHeight;
		if (viewportHeight.value === nextViewportHeight) return;

		viewportHeight.value = nextViewportHeight;

		// Com a barreira armada, o offset virtual depende de spacerExtent, que
		// acabou de mudar junto com a viewport. Rele o scroll real ja no novo
		// sistema de coordenadas antes que um touch desarme a barreira usando um Y
		// antigo (o delta viraria uma faixa vazia no topo do sheet).
		if (hasInitialized.value && barrier.isArmed.value) {
			const rebasedScrollY = readVirtualScrollY();
			scrollY.value = rebasedScrollY;
			lastScrollY = rebasedScrollY;
		}
	}

	function checkScreenSize(): void {
		if (!import.meta.client) return;
		isScreenBelowBreakpoint.value = window.innerWidth < breakpoint;

		// Se está scrollando, adia a aplicação da altura até scrollend dispara.
		// Evita reflow mid-momentum que interrompe inércia.
		if (isScrolling.value) {
			isViewportHeightPending = true;
		} else {
			applyViewportHeight();
		}
	}

	// Define posicao inicial do drawer no mobile.
	// Estado inicial: lista aberta com a NAVBAR VISIVEL. Por isso ancora o topo
	// do sheet logo abaixo do chrome completo (navbar + filtros) — que e o
	// `spacerExtent`, 60px abaixo do snap CLOSED (cujo chrome e so a barra de
	// filtros, com navbar oculta). O primeiro gesto do usuario assume dali.
	function initialPosition(): void {
		if (!isScreenBelowBreakpoint.value) return;
		commitVirtualScroll(geometry.spacerExtent.value);
		hasInitialized.value = true;
		settleBarrier();
	}

	// Re-sincroniza geometria em resize/rotacao/mudanca de snap.
	watch([viewportHeight, geometry.peek, () => snapHeights.value[1]], () => {
		if (!isScreenBelowBreakpoint.value) return;
		if (!hasInitialized.value) return;
		// Em modo leitura, evita saltos de scroll.
		if (isInReadingMode()) return;
		// No iOS, scrollar (especialmente pra cima) mostra/esconde a URL bar e
		// dispara `resize` no meio do gesto/inercia. Re-commitar aqui jogaria o
		// drawer para o snap do nivel atual (CLOSED, junto ao limite) matando a
		// inercia e fazendo o drawer "voltar pro fully open". So re-sincroniza
		// quando o scroll esta ocioso (rotacao real), nao durante um gesto.
		if (
			machine.isDragging.value ||
			machine.isTransitioning.value ||
			animator.isAnimating() ||
			isScrolling.value
		) {
			return;
		}
		const target = geometry.snapScrollY.value[currentLevel.value];
		commitVirtualScroll(target);
	});

	function applyModeClass(on: boolean): void {
		if (!import.meta.client) return;
		document.documentElement.classList.toggle('bottom-sheet-active', on);
	}

	watch(isScreenBelowBreakpoint, below => {
		applyModeClass(below);
		header.resetToVisible();
		if (!below) {
			hasInitialized.value = false;
		}
	});

	// Mudanca de nivel fora de drag/snap (scroll nativo na faixa do drawer):
	// sincroniza navbar e barreira. Durante drag/snap quem cuida disso sao os
	// proprios fluxos de gesto (`settleGesture`/`snapTo`).
	watch(
		currentLevel,
		level => {
			if (!isScreenBelowBreakpoint.value) return;
			if (
				isDragging.value ||
				machine.isTransitioning.value ||
				animator.isAnimating()
			) {
				return;
			}
			// Navbar so acompanha mudanca de nivel causada por gesto do usuario.
			// Saltos sem gesto (restauracao de scroll no F5) atravessam niveis e,
			// sem o guard, escondiam a navbar na carga (faixa branca no topo).
			if (hasPendingListGesture) {
				header.syncToLevel(level);
			}
			settleBarrier();
		},
		{ immediate: true }
	);

	const tracker = createTouchDirectionTracker({
		onPullDown: event => onHandleTouchStart(event)
	});

	// Toque na area do sheet (fora do handle).
	// `listGestureStartY` ancora a direcao do gesto para o snap do scroll nativo.
	//
	// Drag a partir da lista: SO no HALF_OPEN e SO ate o fim do primeiro card
	// (elemento marcado com `data-bottom-sheet-drag-zone`). Nessa faixa do topo o
	// toque vira drag (segue o dedo 1:1 e sempre assenta num snap). Abaixo dela — e
	// em qualquer outro nivel, em especial no CLOSED — a lista rola NATIVAMENTE,
	// preservando a leitura e a retracao da URL bar (a arquitetura depende disso).
	function onSheetTouchStart(event: TouchEvent): void {
		if (!isScreenBelowBreakpoint.value) return;
		// Toque durante o arme gradual: finaliza-o na hora para liberar o scroll.
		finishArmImmediately();

		// Toque durante snap/transicao: cancela e sincroniza para o usuario assumir o controle.
		if (animator.isAnimating() || machine.isTransitioning.value) {
			animator.cancel();
			machine.endSnap();
			scrollY.value = readVirtualScrollY();
		}

		listGestureStartY = scrollY.value;
		hasPendingListGesture = true;
		isAwaitingFirstInteraction = false;

		const level = currentLevel.value;
		// "No topo da lista" = no repouso do fullscreen (CLOSED = maxOffset), nao
		// scrollado para a leitura (scrollY > maxOffset). Compara com maxOffset, nao
		// spacerExtent: com closedTopChromeHeight < topChromeHeight eles diferem (o
		// repouso do CLOSED fica em maxOffset, ~60px acima da ancora).
		const isAtTop = scrollY.value <= geometry.maxOffset.value + SNAP_RESTING_PX;

		// So permitimos interacao na lista se estiver em HALF_OPEN,
		// OU se estiver em CLOSED no topo da lista.
		const canInteractFromList =
			level === SheetLevel.HALF_OPEN ||
			(level === SheetLevel.CLOSED && isAtTop);

		if (!canInteractFromList) return;

		const node = event.target;
		if (node instanceof HTMLElement && node.closest('.bottom-sheet__handle')) {
			return; // o handle ja inicia o proprio drag
		}

		// Procura o marcador dentro do proprio sheet (o elemento do listener), nao
		// no documento inteiro.
		const sheetRoot =
			event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
		const touch = event.touches[0];
		const zone = sheetRoot?.querySelector(`[${BOTTOM_SHEET_DRAG_ZONE_ATTR}]`);
		if (!touch || !(zone instanceof HTMLElement)) return;
		if (touch.clientY > zone.getBoundingClientRect().bottom) return;

		if (level === SheetLevel.HALF_OPEN) {
			// No HALF_OPEN nao ha scroll nativo, drag comeca de imediato.
			onHandleTouchStart(event);
		} else {
			// No CLOSED (topo da lista), aguardamos o primeiro touchmove para discernir
			// se e scroll up (leitura) ou drag down (minimizar).
			tracker.start(event, touch);
		}
	}

	onMounted(() => {
		checkScreenSize();
		applyModeClass(isScreenBelowBreakpoint.value);
		window.addEventListener('resize', checkScreenSize, { passive: true });
		window.addEventListener('scroll', onScroll, { passive: true });
		if (supportsScrollEnd) {
			window.addEventListener('scrollend', onScrollEnd, { passive: true });
		}
		// Ancora ANTES do primeiro paint: o nextTick roda apos o flush do Vue
		// (spacer ja com altura no DOM) e ainda dentro da mesma task — sem
		// frame visivel no peek (flick do mapa durante o loading). Scrolls de
		// roteador/restauracao que chegarem depois sao corrigidos no onScroll
		// enquanto nao houver interacao do usuario.
		void nextTick(() => initialPosition());
	});

	onUnmounted(() => {
		window.removeEventListener('resize', checkScreenSize);
		window.removeEventListener('scroll', onScroll);
		if (supportsScrollEnd) {
			window.removeEventListener('scrollend', onScrollEnd);
		}
		detachWindowDragListeners();
		tracker.stop();
		registry.releaseAll();
		applyModeClass(false);
		animator.cancel();
		machine.endSnap();
		machine.endDrag();
		hasInitialized.value = false;
		// `registry.releaseAll()` ja cancelou o rAF do arme; zera o flag que indica
		// que estavamos dirigindo o scroll para nao deixar estado pendurado.
		isArming = false;

		header.resetToVisible();
		isBottomSheetDragging.value = false;
	});

	return {
		onSheetTouchStart,
		onHandleTouchStart,
		isScreenBelowBreakpoint,
		spacerHeight: barrier.spacerHeight,
		menuBottomLevel: currentLevel,
		currentY: scrollY,
		isSheetFullscreen,
		isDragging,
		isTransitioning,
		moveBackOneLevel,
		moveBackToInitialPosition,
		toggleToClosedWhenOpen,
		toggleToHalfOpenWhenClosed,
		scrollToLevel,
		shouldIgnoreHandleClick
	};
}
